import React, { forwardRef } from 'react'
import DatePicker from 'react-multi-date-picker'
import { DATE_PICKER_SETTINGS } from '../constants/constant'

/**
 * Calendario base compartido para seleccionar una fecha o un rango.
 * Usa los nombres de meses y días en español de DATE_PICKER_SETTINGS,
 * inicia la semana en lunes y no resalta el día actual por defecto.
 *
 * Datos que recibe:
 * - `value`: fecha seleccionada para uso controlado. Acepta Date, DateObject,
 *   string, número o null; con `range`, recibe un arreglo de fechas.
 * - `onChange(value)`: notifica la selección como DateObject o null para una
 *   fecha, y como arreglo de DateObject para un rango. No devuelve objetos Dayjs.
 * - `range` (por defecto `false`): activa la selección de rango y su vista previa
 *   al pasar el cursor por las fechas.
 * - `numberOfMonths`: meses visibles; por defecto uno para fecha y dos para rango.
 * - `className` (por defecto `report-period-calendar`): clase del calendario.
 * - `containerClassName`: clase del contenedor del selector.
 * - `format`: formato de la fecha mostrada y de los valores recibidos como texto.
 * - `minDate`, `maxDate` y `disabled`: límites de selección y estado deshabilitado.
 * - `render(value, openCalendar)`: permite mostrar un botón u otro control propio;
 *   `value` es el texto formateado y `openCalendar()` abre el calendario.
 * - `portal`, `zIndex` y `calendarPosition`: ubicación y superposición del calendario.
 * - `plugins`, `onOpen` y `onClose`: extensiones y eventos del calendario.
 * - `ref`: referencia al DatePicker; permite usar openCalendar() y closeCalendar().
 *
 * Las demás props se pasan a react-multi-date-picker y pueden sobrescribir sus
 * valores predeterminados, como rangeHover, months, weekDays o highlightToday.
 * La pantalla conserva el estado, convierte las fechas y consulta los datos.
 * Para filtros de rango con valores Dayjs, usar DateRangeFilter.
 *
 * Ejemplo de una fecha controlada:
 *   const [fecha, setFecha] = React.useState(null)
 *   <AppDatePicker value={fecha} onChange={setFecha} format="DD/MM/YYYY" />
 *
 * Ejemplo de un rango controlado:
 *   const [rango, setRango] = React.useState([])
 *   <AppDatePicker range value={rango} onChange={setRango} format="DD/MM/YYYY" />
 */
const AppDatePicker = forwardRef(function AppDatePicker(
  { range = false, numberOfMonths = range ? 2 : 1, className = 'report-period-calendar', ...props },
  ref
) {
  return (
    <DatePicker
      ref={ref}
      className={className}
      range={range}
      rangeHover={range}
      numberOfMonths={numberOfMonths}
      months={DATE_PICKER_SETTINGS.months}
      weekDays={DATE_PICKER_SETTINGS.weekDays}
      weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
      highlightToday={false}
      {...props}
    />
  )
})

export default AppDatePicker
